/**
 * PhysiologyBridgeCard — общият физиологичен слой между близнаците.
 * Обем и осмолалитет, електролити и възбудимост, мембранен транспорт,
 * хомеостазни контури, липсващи данни и клинични примери.
 * Данни: GET /api/physiology-bridge?focus=kidney|breast|dental|cortisol
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Waves, Zap, Droplet, ArrowLeftRight, RefreshCw, AlertTriangle, ChevronDown, ChevronUp,
  Moon, Footprints, Flame,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TWIN_BG = {
  kidney: "бъбречен", breast: "гръден", dental: "дентален", cortisol: "кортизолов",
  blood: "хематологичен", nerve: "нервен", sleep: "сън", movement: "движение",
  inflammation: "възпалителен", cognitive: "когнитивен",
};
const STATUS_STYLE = {
  "компенсира": "bg-emerald-500/15 text-emerald-600",
  "претоварен": "bg-rose-500/15 text-rose-600",
  "няма данни": "bg-slate-500/15 opacity-70",
};
const SYS_ICON = { volume: Waves, excitability: Zap, blood: Droplet, nerve: Zap,
  recovery: Moon, load: Footprints, inflammation: Flame };
const BAND_STYLE = {
  "гори": "bg-rose-500/15 text-rose-600",
  "под натиск": "bg-amber-500/15 text-amber-600",
  "държи": "bg-emerald-500/15 text-emerald-600",
  "няма данни": "bg-slate-500/15 opacity-70",
};

export default function PhysiologyBridgeCard({ focus, isDark = false, cardCls = "" }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/physiology-bridge`, { params: focus ? { focus } : {} })
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [focus]);

  if (!d) return null;
  const border = isDark ? "border-white/10" : "border-gray-200";

  return (
    <div className={cardCls || `p-4 rounded-2xl border ${border}`} data-testid="physiology-bridge-card">
      <div className="flex items-start gap-2">
        <ArrowLeftRight className="w-4 h-4 text-cyan-500 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-bold">Физиологичен мост между близнаците</div>
          <p className="text-[11px] opacity-70 mt-0.5">{d.intro_bg}</p>
        </div>
      </div>

      {/* контролни системи */}
      <div className="mt-3 grid md:grid-cols-2 gap-2">
        {d.systems.map((s) => {
          const Icon = SYS_ICON[s.key] || Waves;
          return (
            <div key={s.key} className={`p-3 rounded-xl border ${border}`}
              data-testid={`bridge-system-${s.key}`}>
              <div className="text-[11px] font-bold inline-flex items-center gap-1">
                <Icon className="w-3.5 h-3.5 text-cyan-500" /> {s.title_bg}
              </div>
              <div className="text-[10px] opacity-70 mt-0.5">{s.summary_bg}</div>
              <div className="mt-2 space-y-1.5">
                {s.findings.length === 0 ? (
                  <div className="text-[10px] opacity-55 italic">
                    Няма достатъчно стойности за тази система.
                  </div>
                ) : s.findings.map((f, i) => (
                  <div key={i} className={`p-1.5 rounded-lg ${
                    f.urgency === "high" ? "bg-rose-500/10" : isDark ? "bg-white/5" : "bg-slate-50"}`}>
                    <div className="text-[10px] font-bold">{f.label_bg}</div>
                    <div className="text-[10px] opacity-75">{f.text_bg}</div>
                  </div>
                ))}
              </div>
              <div className="text-[9px] opacity-55 mt-1.5">
                Засяга: {s.twins.map((t) => TWIN_BG[t] || t).join(", ")} близнак
              </div>
            </div>
          );
        })}
      </div>

      {/* iter1041 — кой контур гори днес */}
      {d.loops_ranked?.length > 0 && (
        <div className="mt-3" data-testid="bridge-loops-ranked">
          <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1.5 inline-flex items-center gap-1">
            <Flame className="w-3 h-3 text-rose-500" /> Кой контур гори днес
          </div>
          <p className="text-[11px] font-medium mb-2" data-testid="bridge-hottest">
            {d.hottest_bg}
          </p>
          <div className="space-y-1.5">
            {d.loops_ranked.map((r, i) => (
              <div key={r.key} className={`p-2 rounded-lg border ${
                r.band === "гори" ? "border-rose-500/40 bg-rose-500/5" : border}`}
                data-testid={`bridge-rank-${r.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[11px] font-bold min-w-0">
                    <span className="opacity-50 mr-1">{i + 1}.</span>{r.title_bg}
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                    BAND_STYLE[r.band] || ""}`} data-testid={`bridge-rank-band-${r.key}`}>
                    {r.band}{r.severity != null ? ` ${r.severity}` : ""}
                  </span>
                </div>
                {r.severity != null && (
                  <div className="mt-1 h-1 rounded-full overflow-hidden bg-slate-500/15">
                    <div className="h-full rounded-full"
                      style={{ width: `${r.severity}%`,
                        backgroundColor: r.band === "гори" ? "#f43f5e"
                          : r.band === "под натиск" ? "#f59e0b" : "#22c55e" }} />
                  </div>
                )}
                {r.drivers_bg?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {r.drivers_bg.map((x, j) => (
                      <li key={j} className="text-[10px] opacity-80">• {x}</li>
                    ))}
                  </ul>
                )}
                <div className="text-[10px] mt-1">
                  <span className="font-bold">Какво го сваля: </span>
                  <span className="opacity-85">{r.lever_bg}</span>
                </div>
                <div className="text-[9px] opacity-55 mt-0.5">
                  Ефектор: {r.effector_bg} · дърпа:{" "}
                  {r.twins.map((t) => TWIN_BG[t] || t).join(", ")} близнак
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* хомеостазни контури — анатомията на всеки контур, зад детайлите */}
      {open && (
      <div className="mt-3" data-testid="bridge-loops">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-1.5 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Хомеостазни контури
        </div>
        <div className="space-y-1.5">
          {d.loops.map((l) => (
            <div key={l.key} className={`p-2 rounded-lg border ${border}`}
              data-testid={`bridge-loop-${l.key}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-[11px] font-bold min-w-0">{l.title_bg}</div>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                  STATUS_STYLE[l.status] || ""}`}>{l.status}</span>
              </div>
              <div className="text-[10px] opacity-70 mt-0.5">
                {l.receptor_bg} → {l.centre_bg} → {l.effector_bg}
              </div>
              <div className="text-[10px] opacity-75 mt-0.5">{l.note_bg}</div>
              <div className="text-[9px] opacity-55 mt-0.5">
                Дърпа: {l.twins.map((t) => TWIN_BG[t] || t).join(", ")} близнак
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* липсващи връзки */}
      {d.gaps?.length > 0 && (
        <div className="mt-3 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30"
          data-testid="bridge-gaps">
          <div className="text-[11px] font-bold text-amber-600 dark:text-amber-300 inline-flex items-center gap-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Къде липсва връзка
          </div>
          <div className="mt-1 space-y-1">
            {d.gaps.map((g, i) => (
              <div key={i}>
                <div className="text-[10px] font-bold">{g.title_bg}</div>
                <div className="text-[10px] opacity-75">
                  {g.text_bg} <span className="opacity-60">→ {g.module_bg}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setOpen(!open)}
        className="mt-3 text-[11px] font-bold text-cyan-500 inline-flex items-center gap-1"
        data-testid="bridge-toggle">
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        Мембранен транспорт, контурите отблизо и клинични примери
      </button>
      {open && (
        <div className="mt-2 space-y-3" data-testid="bridge-detail">
          <p className="text-[11px] opacity-80">{d.homeostasis_bg}</p>
          <div className="grid md:grid-cols-2 gap-2">
            {d.transport.map((t) => (
              <div key={t.key} className={`p-2 rounded-lg border ${border}`}
                data-testid={`bridge-transport-${t.key}`}>
                <div className="text-[11px] font-bold">{t.title_bg}</div>
                <div className="text-[10px] opacity-75 mt-0.5">{t.text_bg}</div>
                <div className="text-[9px] opacity-55 mt-0.5">
                  {t.twins.map((x) => TWIN_BG[x] || x).join(", ")} близнак
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="text-[10px] uppercase font-bold opacity-55 mb-1">
              Клинични примери
            </div>
            <div className="space-y-1.5">
              {d.cases.map((c) => (
                <div key={c.key} className={`p-2 rounded-lg border ${border}`}
                  data-testid={`bridge-case-${c.key}`}>
                  <div className="text-[11px] font-bold">{c.title_bg}</div>
                  <div className="text-[10px] opacity-75 mt-0.5">{c.text_bg}</div>
                  <div className="text-[10px] opacity-85 mt-0.5">
                    Какво да проверите: {c.check_bg}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[9px] opacity-50">{d.note_bg}</div>
        </div>
      )}
    </div>
  );
}
